import { FunctionComponent, useCallback, memo } from 'react';
import { IntlShape } from 'react-intl';
import { useFormatMessage } from '../../i18n-review/client/Msg';
import { List } from '@phosphor-icons/react';
import { Icon } from '../../design-system/icons/Icon';
import { trackEvent } from '../../utils/analytics/analytics';
import { usePageContext } from 'vike-react/usePageContext';
import { AutocompleteHandle } from '../Autocomplete';
import { Logo } from '../navigation/Logo';
import Avatar from '../../design-system/Avatar';
import { SearchNavbar } from './SearchNavbar';
import { MyJobsNavEntry } from '../navigation/MyJobsNavEntry';
import { TopFilters } from '../filters/TopFilters';
import { ViewToggle } from '../filters/ViewToggle';
import { useSearchFilters, useSearchQuery, useSearchContext } from '../../contexts/SearchContext';
import { useFacets } from '../../contexts/FacetsContext';
import { useEffectiveUserProfilePhoto } from '../../hooks/useEffectiveUserProfilePhoto';
import { convertUrlToLanguage } from '../../utils/search-router/url-handling';
import { getLocaleFromPathname, normalizeSupportedLocale } from '../../utils/routing/locale-from-pathname';
import { LANG_FLAGS } from '../navigation/LanguageFlags';
import {
  CORE_CONTROL_GHOST,
  CORE_HEADER_STICKY_SRP,
  CORE_NAV_ROW_BORDER,
} from '../../constants/corePublicTheme';
import { useNavbarLiveIndexEnabled } from '../../utils/feature-flags/navbar-live-index-flag';
import { AccountPill } from '../navigation/nav-live-index/AccountPill';
import { BarsMenuButton } from '../navigation/nav-live-index/BarsMenuButton';
import { LanguageSelector } from '../navigation/LanguageSelector';
import { countAppliedFilterValues } from '../navigation/nav-live-index/applied-filter-count';
import { useSearchSort } from '../../contexts/SearchContext';
import { tapeMessages } from '../filters/tape-messages';
import { resolveLocationSegmentValue, formatLocationSegmentLabel } from '../navigation/nav-live-index/location-segment-label';
import { useNewLocationSystemEnabled } from '../../utils/facets/new-location-system-dev-flag';
import { IndexStrip } from '../navigation/nav-live-index/IndexStrip';
import { MobileIndexSubtitle } from '../navigation/nav-live-index/MobileIndexSubtitle';
import type { MenuPulse } from '../../server/routes/menu-pulse';

interface DesktopFilterCategoriesProps {
  intl: IntlShape;
  isMobileMenuOpen?: boolean;
  setIsMobileMenuOpen?: (open: boolean) => void;
  isAllFiltersModalOpen?: boolean;
  setIsAllFiltersModalOpen?: (open: boolean) => void;
  /** Which rail section AllFiltersModal is currently showing (null while closed), so the
   * Location button only highlights when that's genuinely the tab in view. */
  allFiltersModalSection?: string | null;
  clearSearch: (event?: React.MouseEvent<HTMLAnchorElement>) => Promise<void>;
  user?: any;
  closeAutocomplete?: () => void;
  autocompleteRef?: React.RefObject<AutocompleteHandle | null>;
  onAutocompleteOpen?: () => void;
}

const DesktopFilterCategories: FunctionComponent<DesktopFilterCategoriesProps> = ({
  intl,
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  isAllFiltersModalOpen,
  setIsAllFiltersModalOpen,
  allFiltersModalSection,
  clearSearch,
  user,
  closeAutocomplete,
  autocompleteRef,
  onAutocompleteOpen,
}) => {
  const formatMessage = useFormatMessage();
  const { filters } = useSearchFilters();
  const menuAvatarSrc = useEffectiveUserProfilePhoto(user ?? null);
  const pageContext = usePageContext();
  const pathname = (pageContext as any)?.urlPathname ?? (typeof window !== 'undefined' ? window.location.pathname : '');
  const locale = normalizeSupportedLocale(pageContext?.locale ?? intl?.locale ?? getLocaleFromPathname(pathname) ?? 'en');
  // Navbar live-index redesign (TEC navbar redesign Phase 1): seeded from pageContext.
  // navbarLiveIndexEnabled (Phase 1 review fix 2.8, see server/vike-handler.ts), then
  // re-confirmed against the cookie post-hydrate (see useFeatureFlag). Flag off, every branch
  // below renders exactly what it rendered before this ticket.
  const liveIndex = useNavbarLiveIndexEnabled((pageContext as { navbarLiveIndexEnabled?: boolean } | undefined)?.navbarLiveIndexEnabled);
  // Phase 3 item 3.2: server-rendered figures for IndexStrip/MobileIndexSubtitle's first paint.
  const initialPulse = (pageContext as { navPulse?: MenuPulse | null } | undefined)?.navPulse ?? null;
  const useRegionTags = useNewLocationSystemEnabled();

  const filtersObj = (typeof filters === 'object' && filters) ? filters as Record<string, string[]> : {};
  const appliedFilterCount = countAppliedFilterValues(filtersObj);
  // Strip mode (no filters) still tells the visitor when the ordering is not the default, with
  // the same words the tape uses beside "Clear all" once filters are applied.
  const { sort: currentSort } = useSearchSort();
  const sortNote =
    liveIndex && currentSort === 'date'
      ? formatMessage(tapeMessages.sortNewest)
      : liveIndex && currentSort === 'salary'
        ? formatMessage(tapeMessages.sortSalary)
        : null;
  // A facet excluded via the SRP negate-filter UI is a real, results-changing filter
  // (stored as `exclude_<attribute>`) and must count as "active" here too, or the location
  // button misleadingly shows inactive while an exclusion is applied.
  const isExcluded = (key: string) => !!filtersObj[`exclude_${key}`]?.length;
  const locationHasFilters = !!(filtersObj.location?.length || filtersObj.remoteness?.length || isExcluded('location'));
  const locationHasExcluded = isExcluded('location');
  // Phase 1 review fix F3: the fused search bar's location segment shows the chosen place's (or
  // remoteness's) actual name instead of the plain word "Location" once a value is applied.
  const locationSegmentLabel = formatLocationSegmentLabel(resolveLocationSegmentValue(filtersObj, locale, useRegionTags));

  const { query } = useSearchQuery();
  const { facetsData } = useFacets();
  // Phase 2 item 2.4: the unfiltered SRP's row two is the index strip, whose right end swaps
  // the freshness stamp for this same List | Map toggle the filtered tape shows next to
  // "Clear all", read directly from SearchContext since SrpView.tsx (which owns the rest of
  // the SRP's viewMode wiring) is a sibling of this component, not an ancestor.
  const { viewMode, setViewMode } = useSearchContext();
  const locationItems = facetsData.location || [];

  // Filter location facets to only show Finland, Remote, or Global locations (same allow/deny
  // list TopFilters and the location filter modal already use: kept local to this component
  // since it's the one place that needs the *filtered* list, not the raw facet response).
  const filterLocationFacets = useCallback((items: Array<{ value: string; label: string; count: number }>) => {
    const allowedKeywords = [
      'finland', 'suomi', 'finländ',
      'remote', 'etätyö', 'etä', 'distans', 'fjärr',
      'global', 'maailmanlaajuinen', 'världsomfattande',
    ];
    const excludedPatterns = [
      'remote - united states', 'etätyö - yhdysvallat', 'etä - yhdysvallat',
      'distans - usa', 'fjärr - usa', 'distans - förenta staterna', 'fjärr - förenta staterna',
    ];
    const activeLocationValues = filtersObj.location || [];
    return items.filter((item) => {
      if (activeLocationValues.includes(item.value)) return true;
      const lowerValue = item.value.toLowerCase();
      const lowerLabel = item.label.toLowerCase();
      if (excludedPatterns.some((pattern) => lowerValue.includes(pattern) || lowerLabel.includes(pattern))) return false;
      return allowedKeywords.some((keyword) => lowerValue.includes(keyword) || lowerLabel.includes(keyword));
    });
  }, [filtersObj.location]);
  const filteredLocationItems = filterLocationFacets(locationItems);

  // Opens the Advanced Filters modal straight to its Location tab, rather than the standalone
  // location popover: the modal is now the single place location filtering lives, this button is
  // just a shortcut into it. AllFiltersModal reads its initial tab from the same localStorage key
  // it already persists "last section shown" under, so writing it here before opening is enough.
  const handleLocationClick = useCallback(() => {
    closeAutocomplete?.();
    if (typeof window !== 'undefined') {
      localStorage.setItem('allFiltersModal.openSection', 'location');
    }
    setIsAllFiltersModalOpen?.(true);
  }, [closeAutocomplete, setIsAllFiltersModalOpen]);

  return (
    <header className={CORE_HEADER_STICKY_SRP} role="banner">
      {/* Top row: Logo + Searchbar (as wide as possible) + Advanced Filters + Hamburger Menu (lg and above) */}
      <nav
        className={`hidden lg:flex items-center gap-3 px-4 xl:px-20 md:px-10 ${liveIndex ? 'h-[58px] py-0' : 'py-3'} ${CORE_NAV_ROW_BORDER}`}
        aria-label="Main navigation"
      >
        {/* Left side: Logo + Searchbar. The search input itself has no max-width here (removed
            from SearchNavbar): with the category-button row gone, it fills the space instead
            of stopping short of it. The location picker lives inside SearchNavbar, attached to
            the search bar's right edge, rather than as a separate button in this row. The
            Advanced Filters button is also rendered inside SearchNavbar (as its `children` slot)
            so it stays left-aligned with the search bar instead of being pushed right by the
            flex-1 space the capped search bar leaves over. */}
        <SearchNavbar
          intl={intl}
          formatMessage={formatMessage}
          clearSearch={clearSearch}
          autocompleteRef={autocompleteRef}
          onAutocompleteOpen={onAutocompleteOpen}
          onLocationClick={handleLocationClick}
          isLocationActive={!!isAllFiltersModalOpen && allFiltersModalSection === 'location'}
          locationHasFilters={locationHasFilters}
          locationHasExcluded={locationHasExcluded}
          liveIndex={liveIndex}
          locationLabel={locationSegmentLabel ?? undefined}
        >
          {/* Advanced Filters: opens the full filter modal (same one mobile's bottom nav opens):
              everything except location/remoteness, which the search bar's own location picker
              handles directly. Navbar live-index redesign: 40px instead of 52px, plus a count
              badge for the number of active facet values across every filter attribute. */}
          <button
            type="button"
            onClick={() => {
              closeAutocomplete?.();
              setIsAllFiltersModalOpen?.(true);
            }}
            className={`flex items-center justify-center xl:justify-start gap-2 ${liveIndex ? 'h-10 min-w-[40px]' : 'h-[52px] min-w-[52px]'} px-3 xl:px-4 rounded-full text-sm font-medium transition-all duration-300 ease-out cursor-pointer select-none border flex-shrink-0 bg-white/90 dark:bg-[#1F1509]/90 text-[#1E1A14] dark:text-[#F2EADF] border-[#DDD5C8]/70 dark:border-[#4A3520]/80 shadow-sm dark:shadow-none hover:shadow-md dark:hover:shadow-none hover:scale-[1.02] active:scale-[0.98]`}
            aria-label={formatMessage({
              id: 'search.refinements.advancedFilters.aria',
              defaultMessage: 'Advanced Filters',
              description: 'Accessibility label for the button that opens the full filters modal',
            })}
          >
            <Icon name="funnel" useMaskForColor className="w-4 h-4" />
            <span className="hidden xl:inline whitespace-nowrap font-semibold">
              {formatMessage({
                id: 'search.refinements.advancedFilters',
                defaultMessage: 'Advanced Filters',
                description: 'Label for the button that opens the full filters modal',
              })}
            </span>
            {liveIndex && appliedFilterCount > 0 ? (
              <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-jc-accent text-jc-surface text-[11px] font-bold tabular-nums">
                {appliedFilterCount > 99 ? '99+' : appliedFilterCount}
              </span>
            ) : null}
          </button>
        </SearchNavbar>

        {liveIndex ? (
          <div className="flex items-center gap-1.5 flex-shrink-0 ml-auto">
            <LanguageSelector intl={intl} formatMessage={formatMessage} locale={locale} sizeVariant="compact40" />
            <span className="w-[10px]" aria-hidden />
            <AccountPill locale={locale} />
            <BarsMenuButton
              onClick={() => {
                const willOpen = !(isMobileMenuOpen ?? false);
                setIsMobileMenuOpen?.(willOpen);
                if (willOpen && isAllFiltersModalOpen) {
                  setIsAllFiltersModalOpen?.(false);
                }
              }}
              ariaLabel={formatMessage({ id: 'navigation.menu', defaultMessage: 'Menu' })}
            />
          </div>
        ) : (
          /* Right side: My Jobs, language, menu */
          <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
            {/* My Jobs - always visible, same as NavigationNavbar on non-search pages */}
            <div className="hidden sm:flex items-center h-[52px]">
              <MyJobsNavEntry locale={locale} />
            </div>
            {/* Language Selector - FI/SV/EN side by side, only visible on xl screens and above.
                White track matching the row's other pills; each language is a true circle (never
                narrower than tall); the selected one gets its national flag as a 30%-opacity
                background tint instead of a plain highlight. */}
            <div className="hidden xl:flex items-center gap-1 h-[52px] px-1.5 rounded-full border border-[#DDD5C8]/70 dark:border-[#4A3520]/80 bg-white/90 dark:bg-[#1F1509]/90">
              {(['fi', 'sv', 'en'] as const).map((lang) => {
                const isActive = locale === lang;
                const Flag = LANG_FLAGS[lang];
                return (
                  <button
                    key={lang}
                    type="button"
                    onClick={() => {
                      trackEvent({
                        name: 'locale_change',
                        params: {
                          to_locale: lang,
                          from_locale: locale,
                          surface: 'other',
                        },
                      });
                      window.location.href = convertUrlToLanguage(window.location.pathname, lang, intl);
                    }}
                    aria-current={isActive ? 'true' : undefined}
                    aria-label={formatMessage({ id: 'navigation.language', defaultMessage: 'Language', description: 'Button aria-label for language selector in navigation' })}
                    className="relative w-9 h-9 shrink-0 rounded-full overflow-hidden flex items-center justify-center text-xs font-extrabold uppercase cursor-pointer transition-colors"
                  >
                    {isActive && <span className="absolute inset-0 opacity-30"><Flag /></span>}
                    <span
                      className={`relative ${
                        isActive
                          ? 'text-[#1E1A14] dark:text-[#F2EADF] [text-shadow:0_0_4px_white,0_0_4px_white,0_0_6px_white] dark:[text-shadow:0_0_4px_#1F1509,0_0_4px_#1F1509,0_0_6px_#1F1509]'
                          : 'text-jc-ink-secondary hover:text-jc-ink'
                      }`}
                    >
                      {lang.toUpperCase()}
                    </span>
                  </button>
                );
              })}
            </div>
            <button
              className={`flex items-center justify-center gap-2 h-[52px] min-w-[52px] rounded-full cursor-pointer hover:scale-[1.02] active:scale-[0.97] transition-[background-color,color,transform] duration-150 ${CORE_CONTROL_GHOST} ${
                user && menuAvatarSrc ? 'pl-1 pr-2' : 'px-2'
              }`}
              onClick={() => {
                const willOpen = !(isMobileMenuOpen ?? false);
                setIsMobileMenuOpen?.(willOpen);
                // Close All Filters modal if opening mobile menu
                if (willOpen && isAllFiltersModalOpen) {
                  setIsAllFiltersModalOpen?.(false);
                }
              }}
              aria-label={formatMessage({
                id: 'navigation.menu',
                defaultMessage: 'Menu',
              })}
            >
              {user && menuAvatarSrc ? (
                <>
                  <div className="flex-shrink-0 overflow-hidden rounded-full">
                    <Avatar
                      src={menuAvatarSrc}
                      size={32}
                      name={user.name || 'User'}
                    />
                  </div>
                  <List className="w-5 h-5 text-[#3D3529] dark:text-[#C4A882] flex-shrink-0" />
                </>
              ) : (
                <List className="w-6 h-6 text-[#3D3529] dark:text-[#C4A882]" />
              )}
            </button>
          </div>
        )}
      </nav>

      {/* Second row (TEC navbar redesign Phase 2 item 2.1/2.4), flag on: the index strip when no
          filter is applied, the filter tape once one is (both live inside TopFilters/IndexStrip;
          this just picks which one mounts). Flag off: the classic pill row, unconditionally,
          exactly as before this ticket. */}
      {liveIndex && appliedFilterCount === 0 ? (
        <>
          <IndexStrip
            locale={locale}
            pathname={pathname}
            formatMessage={formatMessage}
            initialPulse={initialPulse}
            rightSlot={
              <>
                {sortNote ? <span className="text-xs text-jc-muted whitespace-nowrap mr-3">{sortNote}</span> : null}
                <ViewToggle
                  viewMode={viewMode}
                  onSelectList={() => setViewMode('list')}
                  onSelectMap={() => setViewMode('map')}
                  formatMessage={formatMessage}
                />
              </>
            }
          />
          {/* IndexStrip is desktop-only (see IndexStrip.tsx) and there is no other mobile top bar
              on the SRP (DesktopFilterCategories' row one is `hidden lg:flex`; mobile chrome is
              MobileBottomNav at the bottom only): without this, an unfiltered mobile SRP would
              show no row two at all, a real regression from the classic pill row's always-visible
              suggested-filter chips. This carries the same live figures the spec's mobile
              top-bar subtitle (Phase 1, NavigationNavbar only) shows elsewhere, as the closest
              equivalent; see the Phase 2 report's deviations section.
              Review fix P3: the classic mobile row carried the icon logo as the only home link on
              the mobile SRP; this row lacked it. Same Logo `icon` variant, 44px tap box (the row
              itself stays 36px tall; the tap box overflows it symmetrically, centered), same
              clearSearch behaviour. */}
          <div className="flex lg:hidden items-center h-9 px-4 border-t border-jc-border bg-jc-surface gap-1">
            <Logo clearSearch={clearSearch} variant="icon" locale={locale} className="w-11 h-11 -ml-2 flex-shrink-0" />
            <MobileIndexSubtitle locale={locale} formatMessage={formatMessage} initialPulse={initialPulse} />
          </div>
        </>
      ) : (
        <TopFilters
          intl={intl}
          formatMessage={formatMessage}
          clearSearch={clearSearch}
          facetsData={{
            ...facetsData,
            location: filteredLocationItems,
          }}
          filtersObj={filtersObj}
          query={query}
          liveIndex={liveIndex}
        />
      )}
    </header>
  );
};

// Memoize DesktopFilterCategories to prevent unnecessary re-renders
export const DesktopFilterCategoriesMemo = memo(DesktopFilterCategories);
